import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { Button, withStyles } from '@material-ui/core';
import { connect } from 'react-redux';
import {
  Tooltip,
} from '@material-ui/core';
import {
  InfoOutlined as InfoOutlinedIcon,
  WarningRounded as ErrorRoundedIcon,
} from '@material-ui/icons';
import {
  cloneDeep,
  difference,
  differenceBy,
  every,
  filter,
  find,
  findIndex,
  forEach,
  get,
  intersection,
  isEmpty,
  isUndefined,
  keys,
  lowerCase,
  map,
  some,
  sortBy,
  split,
  values,
} from 'lodash';
import {
  DropDownMenu,
  ErrorContainer,
  FormActionButtons,
  FormButtonGroup,
} from '../../../../components';
import Category from './Category';
import EntityDropTarget from './EntityDropTarget';
import EntityPicker from './EntityPicker';
import {
  ALERT_SUCCESS,
  API_STATUS_INCOMPLETE,
  API_STATUS_NEW,
  RATE_LIMIT_QUOTA_LIST_FIELD,
} from '../../../../constants';
import {
  getApiPolicyTemplateArguments,
  getRateLimitQuotaArguments,
  getRateLimitQuotaEnity,
  getRateLimitQuotaPolicyTemplates,
  hasAdminRole,
  selectedRateLimitQuota,
} from '../../../../utils';
import {
  fetchTemplateCategoryDetailsForMap,
  fetchTemplateCategoryList,
  fetchAPIApplicableTemplateCategoryList,
  fetchCategoryTemplates,
} from '../../../../actions/templateCategory';
import {
  fetchAPISettingsEnforceCategoryOrdering,
} from '../../../../actions/apiSettings';
import {
  getAPIApplicableTemplateCategoryListResults,
  getCategoriesTemplatesMap,
  getIsAccessibleTemplateCategoriesLoaded,
  getIsAPIEditTemplateDataLoaded,
  getTemplateCategoriesDetailsMap,
  getTemplateCategoryListResults,
} from '../../../../reducers/templateCategory';
import {
  getAPISettingsEnforceCategoryOrdering,
} from '../../../../reducers/apiSettings';
import {
  ENTITY_PICKER_TYPE_CATEGORY,
  USAGE_OPTION_AT_LEAST_ONE_OPTION,
  USAGE_OPTION_ALL_TEMPLATES,
  USAGE_OPTION_OPTIONAL,
  CATEGORY_CONFIG_ORDER,
  CATEGORY_CONFIG_USAGE,
  COMPLIANCE_STATUS_COMPLIANT,
  TEMPLATE_ERRORS_FILED_MISSING_REQUIRED_FIELD_VALUE,
  COMPLIANT_TOGGLE_BUTTON_CURRENT,
  COMPLIANT_TOGGLE_BUTTON_COMPLIANT,
  VIOLATION_LEVEL_ADMIN_ERROR,
  VIOLATION_LEVEL_ADMIN_OVERRIDABLE,
  VIOLATION_LEVEL_PUBLISHER_ERROR,
  VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE,
  VIOLATION_LEVEL_NO_VIOLATION,
  getComplianceToggleOptions,
} from './constants';
import styles from './styles';
import {
  NEXT_TEXT,
  SAVE_AND_NEXT_TEXT,
} from '../labels';
import { getI18n } from '../../../../utils/intl';
import WarningIcon from './icons/warningIcon.svg';

import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";

// return the severe compliance level among level1 and level2
const getSevereComplianceLevel = (level1, level2) => {
  const complianceOrder = {
    VIOLATION_LEVEL_NO_VIOLATION: 0,
    VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE: 1,
    VIOLATION_LEVEL_PUBLISHER_ERROR: 2,
    VIOLATION_LEVEL_ADMIN_OVERRIDABLE: 3,
    VIOLATION_LEVEL_ADMIN_ERROR: 4,
  };
  return complianceOrder[level1] > complianceOrder[level2] ? level1 : level2;
};

const isUnavailableTemplate = (template) => (!template.name);

// map to store categories compliance toggle status
const STATE_CATEGORY_COMPLIANCE_TOGGLE_STATUS = 'STATE_CATEGORY_COMPLIANCE_TOGGLE_STATUS';
// map to store current states of categories, used for restoring the state
// when user chooses current state
const STATE_CATEGORY_CURRENT_STATE_LIST = 'STATE_CATEGORY_CURRENT_STATE_LIST';

const getDefaultStateMap = () => ({
  STATE_CATEGORY_COMPLIANCE_TOGGLE_STATUS: '',
  STATE_CATEGORY_CURRENT_STATE_LIST: undefined,
});

export const CategoryPolicyTemplates = (props) => {
  const {
    additionalCategoriesDetailsMap,
    allAccessibleTemplateCategories,
    apiDetails,
    apiApplicableTemplateCategories,
    apiUuid,
    categoriesTemplatesMapParam,
    classes,
    enforceOrderingSetting,
    errors,
    fetchAPIApplicableTemplateCategoryList,
    fetchAPISettingsEnforceCategoryOrdering,
    fetchCategoryTemplates,
    fetchRateLimitQuotas,
    fetchTemplateCategoryDetailsForMap,
    fetchTemplateCategoryList,
    goToNextTab,
    isAccessibleTemplateCategoriesLoaded,
    isAPIEditTemplateDataLoaded,
    onCancel,
    rateLimitQuotas,
    saveApiPolicyEntities,
    tabValue,
    userContext,
    featureFlagForDistributeRateLimit,
    notifyMessages,
  } = props;

  const intl = getI18n(useIntl());
  const { compliance, portalStatus, policyEntities } = apiDetails;
  // The map which contains templates associated via categories
  // Might contain categories which are no longer associated when user asks
  // the UI to make API compliant by removing non-compliant categories and correcting
  // categories order
  const [apiCategoriesTemplatesList, setApiCategoriesTemplatesList] = useState([]);
  const [categoryStateMapList, setCategoryStateMapList] = useState([]);
  // API level compliance toggle status
  const [APICompliantToggleStatus, setAPICompliantToggleStatus]
    = useState(COMPLIANT_TOGGLE_BUTTON_CURRENT);
  // overall form validation stae
  const [formValidationState, setFormValidationState] = useState();
  const [formHasTemplateErrors, setFormHasTemplateErrors] = useState(false);
  const [currentModeCategoriesRenderOrder, setCurrentModeCategoriesRenderOrder] = useState([]);
  const [savedAPICurrentState, setSavedAPICurrentState] = useState({});
  const [compliantModeCategoriesRenderOrder, setCompliantModeCategoriesRenderOrder] = useState([]);
  const [expandAllTemplates, setExpandAllTemplates] = useState(false);
  const [showCategoryDropTargets, setShowCategoryDropTargets] = useState(false);
  const [argumentClipboard, setArgumentClipboard] = useState({});
  const [isAllCategoriesDetailsLoaded, setIsAllCategoriesDetailsLoaded] = useState(false);

  const onFormEdit = () => {
    localStorage.setItem('isUnSavedChanges', true);
  };

  const isFormEdited = () => localStorage.getItem('isUnSavedChanges');

  const additionalCategoriesIdsList = useMemo(() => {
    if (isAccessibleTemplateCategoriesLoaded && policyEntities) {
      const associatedCategoriesIdsMap = {};
      forEach(policyEntities, entity => {
        if (entity.templateCategoryUuid) {
          associatedCategoriesIdsMap[entity.templateCategoryUuid] = true;
        }
      });
      const associatedCategoriesIds = keys(associatedCategoriesIdsMap);
      const allAccessibleTemplateCategoriesIds =
        map(allAccessibleTemplateCategories || [], item => item.uuid);
      return difference(associatedCategoriesIds, allAccessibleTemplateCategoriesIds);
    }
    return [];
  }, [allAccessibleTemplateCategories, isAccessibleTemplateCategoriesLoaded, policyEntities]);

  useEffect(() => {
    if (isAccessibleTemplateCategoriesLoaded && policyEntities) {
      if (additionalCategoriesIdsList.length === 0) {
        setIsAllCategoriesDetailsLoaded(true);
      } else {
        forEach(additionalCategoriesIdsList, id => {
          fetchTemplateCategoryDetailsForMap(id);
        })
      }
    }
  }, [additionalCategoriesIdsList, isAccessibleTemplateCategoriesLoaded, policyEntities]);

  useEffect(() => {
    const loadedAdditionalCategoriesIds = keys(additionalCategoriesDetailsMap);
    if (isAccessibleTemplateCategoriesLoaded && policyEntities &&
      (additionalCategoriesIdsList.length === loadedAdditionalCategoriesIds.length)) {
        setIsAllCategoriesDetailsLoaded(true);
    }
  }, [additionalCategoriesDetailsMap, additionalCategoriesIdsList,
  isAccessibleTemplateCategoriesLoaded]);

  const allTemplateCategories = useMemo(() => {
    if (isAllCategoriesDetailsLoaded) {
      const additionalCategories = values(additionalCategoriesDetailsMap);
      const categoriesTobeIncluded = differenceBy(
        additionalCategories, allAccessibleTemplateCategories, item => item.uuid,
      );
      const allItems = [...cloneDeep(allAccessibleTemplateCategories),
        ...cloneDeep(categoriesTobeIncluded)];
      return sortBy(allItems, item => item.ordinal);
    }
    return [];
  }, [additionalCategoriesDetailsMap, allAccessibleTemplateCategories,
    isAllCategoriesDetailsLoaded]);

  const defaultCategory = useMemo(() => {
    return allTemplateCategories && find(allTemplateCategories, item => {
      return item && item.defaultCategory;
    });
  }, [allTemplateCategories]);

  const isAscending = (orderArray) => {
    return every(orderArray, (item, index) => {
      return (index === 0 || item >= orderArray[index - 1]);
    });
  }

  const isStrictlyAscending = (orderArray) => {
    return every(orderArray, (item, index) => {
      return (index === 0 || item > orderArray[index - 1]);
    });
  }

  // adds secondary text to RLQ
  const getRateLimitQuotasForForm = (rateLimitQuotas = []) => rateLimitQuotas.map(item => {
    const limit = get(item, 'rateLimit.maxRequestsPerSecond') ?
      intl.getI18nMessage('label.api.templates.rate.limit', { rateLimit: get(item, 'rateLimit.maxRequestsPerSecond') })
        :
      intl.getI18nMessage('label.none');
    const quota = get(item, 'quota.quota') ? `${get(item, 'quota.quota')} / ${lowerCase(get(item, 'quota.interval'))}`
      : intl.getI18nMessage('label.none');
    const distributeRate = get(item, 'distributedRateLimit.maxRequests') ?
        get(item, 'distributedRateLimit.interval') === 'MINUTE' ?
        intl.getI18nMessage('label.api.templates.distrubute.rate.limit.min',
        { distributedRateLimit: get(item, 'distributedRateLimit.maxRequests') })
        : intl.getI18nMessage('label.api.templates.distrubute.rate.limit.sec',
        { distributedRateLimit: get(item, 'distributedRateLimit.maxRequests') })
      : intl.getI18nMessage('label.none');
    item.secondaryText = featureFlagForDistributeRateLimit ?
      intl.getI18nMessage('label.api.templates.distribute.rate.limit.secondary.text', { distributeRate, limit, quota })
    : intl.getI18nMessage('label.api.templates.rate.limit.secondary.text', { limit,quota });
    return item;
  });

  const categoriesTemplatesMap = useMemo(() => {
    const tempValue = cloneDeep(categoriesTemplatesMapParam || {});
    forEach(tempValue, (categoryTemplates) => {
      forEach(categoryTemplates, (template) => {
        const rateLimitPolicyEntity = getRateLimitQuotaPolicyTemplates(template.arguments);
        if(rateLimitPolicyEntity) {
          const rateLimitQuotasData = getRateLimitQuotasForForm(rateLimitQuotas);
          template.arguments =
            getRateLimitQuotaArguments(rateLimitQuotasData, {}, false, template.arguments,
              featureFlagForDistributeRateLimit);
        }
      });
    });
    return tempValue;
  }, [categoriesTemplatesMapParam]);

  const mergeEntityArgumentsAndCategoryTemplateEntityArguments =
    (entityPolicyArguments, categoryTemplateEntityArguments) => {
      let args = [];
      let apiPolicyTemplateArguments = {};
      const rateLimitPolicyEntity =
        getRateLimitQuotaPolicyTemplates(entityPolicyArguments);
      if(rateLimitPolicyEntity) {
        const rateLimitQuota = selectedRateLimitQuota(rateLimitQuotas,
          rateLimitPolicyEntity.value);
        const rateLimitQuotaEntity = getRateLimitQuotaEnity(rateLimitQuota, false,
          featureFlagForDistributeRateLimit );
        const rateLimitQuotasForForm = getRateLimitQuotasForForm(rateLimitQuotas);
        args = getRateLimitQuotaArguments(
            rateLimitQuotasForForm, {}, false, categoryTemplateEntityArguments,
            featureFlagForDistributeRateLimit,
          );
        apiPolicyTemplateArguments = getApiPolicyTemplateArguments(rateLimitQuotaEntity);
      } else {
        args = cloneDeep(categoryTemplateEntityArguments);
        apiPolicyTemplateArguments =
          getApiPolicyTemplateArguments(entityPolicyArguments || []);
      }

      forEach(args, (item, index) => {
        args[index].value = apiPolicyTemplateArguments[item.name];
      });
      return args;
    };
  
  const initStateLists = (size) => {
    const newStateMap = new Array(size);
    forEach(newStateMap, (item, index) => {
      newStateMap[index] = getDefaultStateMap();
    });
    setCategoryStateMapList(newStateMap);
  };

  // prepare list of categories associated with API
  const [originalAPITemplateCategoriesIds,
    originalAPITemplateCategories, originalAPICategoriesTemplatesList] = useMemo(() => {
    if (!policyEntities || (!allTemplateCategories || allTemplateCategories.length === 0)) {
      return [[], [], []];
    }
    let lastCategoryId;
    const categoriesIds = [];
    const apiCategoriesTemplatesList = [];
    forEach(policyEntities, policyEntity => {
      const categoryId = policyEntity.templateCategoryUuid
        || (defaultCategory && defaultCategory.uuid);
      if (categoryId !== lastCategoryId) {
        categoriesIds.push(categoryId);
        lastCategoryId = categoryId;
        apiCategoriesTemplatesList.push([]);
      }
      const categoryTemplates = categoriesTemplatesMap[categoryId] || [];
      const entitiesArray = apiCategoriesTemplatesList[apiCategoriesTemplatesList.length - 1];
      let categoryTemplatesEntity = find(categoryTemplates,
        item => item.uuid === policyEntity.policyEntityUuid) ||
        { arguments: [], uuid: policyEntity.policyEntityUuid };
      categoryTemplatesEntity.ordinalInAPICategory = entitiesArray.length + 1;

      const mergedArgs = mergeEntityArgumentsAndCategoryTemplateEntityArguments(
          policyEntity.policyTemplateArguments, categoryTemplatesEntity.arguments,
        );
      categoryTemplatesEntity = {
        ...categoryTemplatesEntity,
        arguments: mergedArgs,
      };
      entitiesArray.push(categoryTemplatesEntity);
      apiCategoriesTemplatesList[apiCategoriesTemplatesList.length - 1] = entitiesArray;
    });
    const categories = map(
      categoriesIds, id => find(allTemplateCategories, item => item.uuid === id),
    );
    initStateLists(apiCategoriesTemplatesList.length);
    return [categoriesIds, categories, apiCategoriesTemplatesList];
  }, [allTemplateCategories, categoriesTemplatesMap, policyEntities, rateLimitQuotas]);

  const APIApplicableTemplateCategoriesIds = useMemo(() =>
      map(apiApplicableTemplateCategories, item => item.uuid), [apiApplicableTemplateCategories],
    );

  // The array which contains list of categories to be rendered in the UI
  // Changes based on APICompliantToggleStatus if the API level compliant toggle is there
  const categoriesOrderToRender = (APICompliantToggleStatus === COMPLIANT_TOGGLE_BUTTON_COMPLIANT) ?
    compliantModeCategoriesRenderOrder : currentModeCategoriesRenderOrder;


  // Checking whether the current categories are in order before edit
  const APIHasCategoriesInOrder = useMemo(() => {
    if ((!allTemplateCategories) || (allTemplateCategories.length === 0)
      || (categoriesOrderToRender.length === 0)) {
      return true;
    }
    // filter out flexible categories
    const categories = filter(map(
      categoriesOrderToRender, id => find(allTemplateCategories, item => item.uuid === id),
    ), item => !item.flexible);
    const categoriesOrder = map(categories, item => item && item.ordinal);
    return isAscending(categoriesOrder);
  }, [allTemplateCategories, categoriesOrderToRender]);

  // Checking whether atleast one template is associated.
  // API should have atleast one template associated
  // Unavailable templates(templates which are deleted in sync process) should be
  // removed before save
  const [APIHasAtLeastOneTemplate, APIHasUnavailableTemplate] = useMemo(() => {
    let hasTemplate = false;
    let hasUnavailableTemplate = false;
    forEach(categoriesOrderToRender, (uuid, index) => {
      forEach(apiCategoriesTemplatesList[index], apiCategoryTemplate => {
        hasTemplate = true
        if (isUnavailableTemplate(apiCategoryTemplate)) {
          hasUnavailableTemplate = true;
        }
      });
    });
    return [hasTemplate, hasUnavailableTemplate]
  }, [apiCategoriesTemplatesList, categoriesOrderToRender]);
  
  // Check whether the currentModeCategoriesRenderOrder has
  // non-compliant category associated with it
  const currentModeHasNonApplicableCategories = useMemo(() => {
    const nonApplicableCategoriesIds =
      difference(currentModeCategoriesRenderOrder, APIApplicableTemplateCategoriesIds);
    return nonApplicableCategoriesIds.length > 0;
  }, [currentModeCategoriesRenderOrder]);

  // Check whether the API has templates associated via non-compliant category
  const APIHasNonApplicableCategories = useMemo(() => {
    const nonApplicableCategoriesIds =
      difference(categoriesOrderToRender, APIApplicableTemplateCategoriesIds);
    return nonApplicableCategoriesIds.length > 0;
  }, [categoriesOrderToRender, APIApplicableTemplateCategoriesIds]);

  const isNonApplicableCategoryId = (id) => !APIApplicableTemplateCategoriesIds.includes(id);

  const allTemplateCategoriesMap = useMemo(() => {
    const result = {};
    forEach(allTemplateCategories, category => {
      result[category.uuid] = category;
    });
    return result;
  }, [allTemplateCategories]);

  // Checking whether the categories were in order to show compliant toggle at API level
  const currentModeHasCategoriesInOrder = useMemo(() => {
    const categories = map(currentModeCategoriesRenderOrder, id => allTemplateCategoriesMap[id]);
    const fixedCategories = filter(categories, item => !item.flexible);
    const categoriesOrder = map(fixedCategories, item => item.ordinal);
    return isAscending(categoriesOrder);
  }, [currentModeCategoriesRenderOrder]);

  const allFixedCategoriesIdMap = useMemo(() => {
    const result = {};
    forEach(allTemplateCategories, category => {
      result[category.uuid] = !category.flexible;
    });
    return result;
  }, [allTemplateCategories]);

  const allFlexibleCategoriesIdMap = useMemo(() => {
    const result = {};
    forEach(allTemplateCategories, category => {
      result[category.uuid] = category.flexible;
    });
    return result;
  }, [allTemplateCategories]);

  const isFixedCategoryId = (id) => allFixedCategoriesIdMap[id];

  const isFlexibleCategoryId = (id) => allFlexibleCategoriesIdMap[id];

  const isFixedCategory = (category) => !category.flexible;

  const arrayHasDuplicatedIds = (array) => (array.length !== new Set(array).size);

  const isCategoryUsageOptional =
    (category) => (category.templateUsageRestriction === USAGE_OPTION_OPTIONAL);
  
  const apiApplicableFlexibleMissingCategories = useMemo(() => {
    const applicableFlexCategories =
      filter(apiApplicableTemplateCategories, item => isFlexibleCategoryId(item.uuid));
    const manadatoryCategories = filter(applicableFlexCategories,
      (category) => !isCategoryUsageOptional(category));
    const missingCategories = filter(manadatoryCategories,
      category => !categoriesOrderToRender.includes(category.uuid));
    return missingCategories;
  }, [apiApplicableTemplateCategories, categoriesOrderToRender]);

  const APICategoriesMissing = apiApplicableFlexibleMissingCategories.length > 0;
  const [APICategoriesMissingViolationLevel, setAPICategoriesMissingViolationLevel]
    = useState(VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE);

  const getDuplicatedIds = (ids) => {
    const countMap = {};
    forEach(ids, id => {
      if (countMap[id]) {
        countMap[id] = countMap[id] + 1
      } else {
        countMap[id] = 1;
      }
    });
    const result = [];
    forEach(countMap, (value, key) => {
      if (value > 1) {
        result.push(key);
      }
    });
    return result;
  };

  const APIHasDuplicateFixedCategories = useMemo(() => {
    const fixedCategoriesIds = filter(categoriesOrderToRender, id => isFixedCategoryId(id));
    return arrayHasDuplicatedIds(fixedCategoriesIds);
  }, [categoriesOrderToRender]);

  // show showAPILevelCompliantSwitch when api categories are wrong order or
  // contains a category which is not applicable
  const showAPILevelCompliantSwitch = !currentModeHasCategoriesInOrder ||
    currentModeHasNonApplicableCategories || APIHasDuplicateFixedCategories;

  useEffect(() => {
    if(tabValue === 'policy-templates-tab') {
      fetchAPIApplicableTemplateCategoryList(apiUuid);
      fetchAPISettingsEnforceCategoryOrdering();
      fetchRateLimitQuotas();
      fetchTemplateCategoryList();
      setIsAllCategoriesDetailsLoaded(false);
      setCategoryStateMapList([]);
    }
  }, [tabValue]);

  const addCategoryToStateLists = (categoryUuid, index) => {
    setAPICompliantToggleStatus(COMPLIANT_TOGGLE_BUTTON_CURRENT);
    const categoryToBeAdded = find(allTemplateCategories, item => item.uuid === categoryUuid);
    const defaultStatesMap = getDefaultStateMap(categoryToBeAdded);
    if (!isUndefined(index)) {
      setCurrentModeCategoriesRenderOrder(cloneDeep([
        ...categoriesOrderToRender.slice(0, index),
        categoryUuid,
        ...categoriesOrderToRender.slice(index),
      ]));
      setApiCategoriesTemplatesList(cloneDeep([
        ...apiCategoriesTemplatesList.slice(0, index),
        [],
        ...apiCategoriesTemplatesList.slice(index),
      ]));
      setCategoryStateMapList(cloneDeep([
        ...categoryStateMapList.slice(0, index),
        defaultStatesMap,
        ...categoryStateMapList.slice(index),
      ]));
    } else if (!categoryToBeAdded.flexible) {
      const renderedCategories = map(categoriesOrderToRender,
        id => find(allTemplateCategories, item => item.uuid === id));
      const indexToInsert =
        findIndex(renderedCategories, category => category.ordinal > categoryToBeAdded.ordinal);
      if (indexToInsert === -1) {
        setCurrentModeCategoriesRenderOrder(
          [...categoriesOrderToRender, categoryUuid],
        );
        setApiCategoriesTemplatesList([...cloneDeep(apiCategoriesTemplatesList), []]);
        setCategoryStateMapList([...cloneDeep(categoryStateMapList), defaultStatesMap]);
      } else if (indexToInsert === 0) {
        setCurrentModeCategoriesRenderOrder(
          [categoryUuid, ...categoriesOrderToRender],
        );
        setApiCategoriesTemplatesList([[], ...cloneDeep(apiCategoriesTemplatesList)]);
        setCategoryStateMapList([defaultStatesMap, ...cloneDeep(categoryStateMapList)]);
      } else {
        setCurrentModeCategoriesRenderOrder(cloneDeep([
          ...categoriesOrderToRender.slice(0, indexToInsert),
          categoryUuid,
          ...categoriesOrderToRender.slice(indexToInsert),
        ]));
        setApiCategoriesTemplatesList(cloneDeep([
          ...apiCategoriesTemplatesList.slice(0, indexToInsert),
          [],
          ...apiCategoriesTemplatesList.slice(indexToInsert),
        ]));
        setCategoryStateMapList(cloneDeep([
          ...categoryStateMapList.slice(0, indexToInsert),
          defaultStatesMap,
          ...categoryStateMapList.slice(indexToInsert),
        ]));
      }
    } else {
      setCurrentModeCategoriesRenderOrder(
        [...categoriesOrderToRender, categoryUuid],
      );
      setApiCategoriesTemplatesList([...cloneDeep(apiCategoriesTemplatesList), []]);
      setCategoryStateMapList([...cloneDeep(categoryStateMapList), defaultStatesMap]);
    }
  };

  const removeCategoryFromStateLists = (index) => {
    setAPICompliantToggleStatus(COMPLIANT_TOGGLE_BUTTON_CURRENT);
    categoriesOrderToRender.splice(index, 1);
    setCurrentModeCategoriesRenderOrder(cloneDeep(categoriesOrderToRender));
    apiCategoriesTemplatesList.splice(index, 1)
    setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));
    categoryStateMapList.splice(index, 1);
    setCategoryStateMapList(cloneDeep(categoryStateMapList));
  };

  const moveCategoryInStateLists = (dragIndex, dropIndex) => {
    setAPICompliantToggleStatus(COMPLIANT_TOGGLE_BUTTON_CURRENT);
    let removedItems = categoriesOrderToRender.splice(dragIndex, 1);
    categoriesOrderToRender.splice(dropIndex, 0, removedItems[0]);
    setCurrentModeCategoriesRenderOrder(cloneDeep(categoriesOrderToRender));

    removedItems = apiCategoriesTemplatesList.splice(dragIndex, 1);
    apiCategoriesTemplatesList.splice(dropIndex, 0, removedItems[0]);
    setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));

    removedItems = categoryStateMapList.splice(dragIndex, 1);
    categoryStateMapList.splice(dropIndex, 0, removedItems[0]);
    setCategoryStateMapList(cloneDeep(categoryStateMapList));
  };

  // categories render order if current mode is chosen
  useEffect(() => {
    if (!allTemplateCategories || (allTemplateCategories.length === 0)) {
      return;
    }

    const fixedCategories = filter(originalAPITemplateCategories, item => !item.flexible);
    const categoriesOrder =
      map(fixedCategories, item => item && item.ordinal);
    const categoriesWereInOrder = isAscending(categoriesOrder);
    // add fixed categories whose usage is ON
    const unAssociatedCategoriesIds =
      difference(APIApplicableTemplateCategoriesIds, originalAPITemplateCategoriesIds);
    const unAssociatedCategories =
      map(unAssociatedCategoriesIds, id => allTemplateCategoriesMap[id]);
    // adding only fixed categories as flexible categories are added through entity picker
    const unAssociatedFixedCategories = filter(unAssociatedCategories, isFixedCategory);
    const categoriesTobeIncluded =
      filter(unAssociatedFixedCategories, category => !isCategoryUsageOptional(category));
    const categoriesTobeIncludedIds = map(categoriesTobeIncluded, item => item.uuid);

    // if no categories are associated and no categories have usage enforced,
    // and only one applicable category is there, add that category id, so that
    // user would see atleast one category instead of empty list
    if ((originalAPITemplateCategories.length === 0) && (categoriesTobeIncludedIds.length === 0)
      && (APIApplicableTemplateCategoriesIds.length === 1)) {
      categoriesTobeIncludedIds.push(APIApplicableTemplateCategoriesIds[0]);
    }

    let categoriesRenderOrder = [];
    // If already associated categories are in order, insert unassociated categories
    // as per the order. Else, they will be shown at the end/
    // Update originalAPICategoriesTemplatesList also to call
    // setApiCategoriesTemplatesList with that.
    let updateListLength = 0;
    if (categoriesWereInOrder && categoriesTobeIncludedIds.length > 0) {
      const originalCategories = cloneDeep(originalAPITemplateCategories);
      const categoriesTobeIncluded =
        cloneDeep(map(categoriesTobeIncludedIds,
            id => find(allTemplateCategories, item => id === item.uuid)),
          );
      forEach(categoriesTobeIncluded, (item) => {
        if (!item.flexible) {
          const indexToInsert =
          findIndex(originalCategories, category => category.ordinal > item.ordinal);
          if (indexToInsert === -1) {
            originalCategories.push(item);
            originalAPICategoriesTemplatesList.push([]);
          } else if (indexToInsert === 0) {
            originalCategories.unshift(item);
            originalAPICategoriesTemplatesList.unshift([]);
          } else {
            originalCategories.splice(indexToInsert, 0, item);
            originalAPICategoriesTemplatesList.splice(indexToInsert, 0, []);
          }
        } else {
          originalCategories.push(item);
          originalAPICategoriesTemplatesList.push([]);
        }
      });
      categoriesRenderOrder = map(originalCategories, item => item.uuid);
      setApiCategoriesTemplatesList(originalAPICategoriesTemplatesList);
      updateListLength = originalAPICategoriesTemplatesList.length;
    } else {
      categoriesRenderOrder =
        [...originalAPITemplateCategoriesIds, ...categoriesTobeIncludedIds];
      const arrayToBeAppened = Array(categoriesTobeIncludedIds.length).fill([]);
      setApiCategoriesTemplatesList([...originalAPICategoriesTemplatesList, ...arrayToBeAppened]);
      updateListLength = originalAPICategoriesTemplatesList.length + arrayToBeAppened.length;
    }
    // update state lists as we are updating apiCategoriesTemplatesList
    initStateLists(updateListLength);
    setCurrentModeCategoriesRenderOrder(categoriesRenderOrder);
  }, [allTemplateCategories, APIApplicableTemplateCategoriesIds,
    originalAPITemplateCategories, originalAPITemplateCategoriesIds]);

  const getOrderValidationErrors = (category, index) => {
    const { enforceTemplateOrdering } = category;
    if (!enforceTemplateOrdering) {
      return false;
    }
    const selectedTemplates = apiCategoriesTemplatesList[index];
    // consider fixed templates only for validating order
    const selectedFixedTemplates = filter(selectedTemplates, item => !item.flexible);
    const orderArray = map(selectedFixedTemplates, item => item.ordinal);
    // using isStrictlyAscending instead of isAscending
    // as we don't want to allow duplicate fixed templates
    return !isStrictlyAscending(orderArray);
  };

  const getUsageValidationErrors = (category, index) => {
    const { templateUsageRestriction, uuid } = category;
    if (isCategoryUsageOptional(category)) {
      return false;
    }
    const categoryTemplates = categoriesTemplatesMap[uuid];
    const categoryTemplatesIds = map(categoryTemplates, item => item.uuid);
    const selectedTemplates = apiCategoriesTemplatesList[index];
    const selectedTemplatesIds = map(selectedTemplates, item => item.uuid);

    if (templateUsageRestriction === USAGE_OPTION_AT_LEAST_ONE_OPTION) {
      return (categoryTemplatesIds.length > 0 &&
        !(intersection(categoryTemplatesIds, selectedTemplatesIds).length));
    } else if (templateUsageRestriction === USAGE_OPTION_ALL_TEMPLATES) {
      const availableCategoryTemplates = filter(categoryTemplates, item => item.name);
      const availableCategoryTemplatesIds = map(availableCategoryTemplates, item => item.uuid);
      return some(availableCategoryTemplatesIds, id => !selectedTemplatesIds.includes(id));
    }
    return true;
  };

  const validateForm = (categoriesErrorsList, categoryWiseTemplatesErrorsMap) => {

      let overAllComplianceLevel = VIOLATION_LEVEL_NO_VIOLATION;
      // treat missing values for required fields, unavailable templates,
      // no template(or no category) association and assoication of noncompliant
      // categories as an error
      let hasMissingRequiredFields = false;
      forEach(categoryWiseTemplatesErrorsMap, categoryTemplatesErrors => {
        forEach(categoryTemplatesErrors, templateArgumentsErros => {
          forEach(templateArgumentsErros, templateArgumentErros => {
            if (templateArgumentErros[TEMPLATE_ERRORS_FILED_MISSING_REQUIRED_FIELD_VALUE]) {
              hasMissingRequiredFields = true;
            }
          });
        });
      });

      if (APIHasUnavailableTemplate || !APIHasAtLeastOneTemplate ||
        hasMissingRequiredFields || APIHasNonApplicableCategories ||
        APIHasDuplicateFixedCategories) {
          overAllComplianceLevel = VIOLATION_LEVEL_ADMIN_ERROR;
      }

      if (APIHasUnavailableTemplate || hasMissingRequiredFields || !APIHasAtLeastOneTemplate) {
        setFormHasTemplateErrors(true);
      } else {
        setFormHasTemplateErrors(false);
      }

      // API level violation:
      // if categories are in incorrect order, check enforceOrderingSetting
      if (!APIHasCategoriesInOrder) {
        const categoryOrderingComplianceLevel
          = getAPILevelRuleViolationComplianceLevel(portalStatus, compliance);
        // If ordering violation is severe than overAllComplianceLevel,
        // then assign that to overAllComplianceLevel
        overAllComplianceLevel =
          getSevereComplianceLevel(overAllComplianceLevel, categoryOrderingComplianceLevel);
      }

      // Check category level violations:
      // If a category level violation is severe than overAllComplianceLevel,
      // then assign that to overAllComplianceLevel
      forEach(categoriesOrderToRender, (uuid, index) => {
        const instances = filter(categoriesOrderToRender, id => id === uuid).count;
        const isDuplicatedFixedCategoryId = isFixedCategoryId(uuid) && (instances > 1);
        const isNonCompliantCategory = isNonApplicableCategoryId(uuid) ||
          isDuplicatedFixedCategoryId;
        // ignore violations of non-compliant category as the presence
        // of templates from those categories handled by APIHasNonApplicableCategories
        // and APIHasDuplicateFixedCategories
        if (!isNonCompliantCategory) {
          const categoryErrrosMap = categoriesErrorsList[index] || {};
          const complianceLevel =
            getCategoryLevelRuleViolationComplianceLevel(uuid, portalStatus, compliance);
          if (categoryErrrosMap[CATEGORY_CONFIG_ORDER]
            || categoryErrrosMap[CATEGORY_CONFIG_USAGE]) {
              overAllComplianceLevel =
                getSevereComplianceLevel(overAllComplianceLevel, complianceLevel);
          }
        }
      });

      if (APICategoriesMissing) {
        let violationLevel = VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE;
        // go through all missing categories configured violation severity level and
        // choose greater severity value
        forEach(apiApplicableFlexibleMissingCategories, category => {
          const complianceLevel =
            getCategoryLevelRuleViolationComplianceLevel(category.uuid, portalStatus, compliance);
          violationLevel = getSevereComplianceLevel(violationLevel, complianceLevel);
          overAllComplianceLevel =
            getSevereComplianceLevel(overAllComplianceLevel, complianceLevel);
        });
        setAPICategoriesMissingViolationLevel(violationLevel);
      }

      return overAllComplianceLevel;
  };

  const categoriesValidationMapList = useMemo(() => {
    const validationErrorsList = [];
    forEach(categoriesOrderToRender, (uuid, index) => {
      const errorsObj = {};
      const category = find(allTemplateCategories, item => item.uuid === uuid) || {};
      errorsObj[CATEGORY_CONFIG_ORDER] = getOrderValidationErrors(category, index);
      errorsObj[CATEGORY_CONFIG_USAGE] = getUsageValidationErrors(category, index);
      validationErrorsList.push(errorsObj);
    });
    return validationErrorsList;
  }, [categoriesOrderToRender, apiCategoriesTemplatesList]);

  const categoryTemplatesErrorsArrayList = useMemo(() => {
    const templatesErrorList
      = new Array(categoriesOrderToRender.length).fill([]);
    forEach(categoriesOrderToRender, (uuid, index) => {
      const categoryTemplatesErrors = [];
      const currentAPICategoryTemplates = apiCategoriesTemplatesList[index];
      forEach(currentAPICategoryTemplates, currentTemplate => {
        const currentTemplateErrors = [];
        const currentTemplateArguments = currentTemplate.arguments;
        forEach(currentTemplateArguments, currentArgument => {
          const currentArgumentErrors = {};
          const { label, required, value } = currentArgument;
          let isValueEmpty = !value;
          if (typeof value === 'string') {
            isValueEmpty = !value.trim().length;
          } else if (typeof value === 'object') {
            isValueEmpty = isEmpty(value);
          }
          if (label && required && isValueEmpty) {
            currentArgumentErrors[TEMPLATE_ERRORS_FILED_MISSING_REQUIRED_FIELD_VALUE] = true;
          }
          currentTemplateErrors.push(currentArgumentErrors);
        });
        categoryTemplatesErrors.push(currentTemplateErrors);
      });
      templatesErrorList[index] = categoryTemplatesErrors;
    });
    return templatesErrorList;
  }, [categoriesOrderToRender, apiCategoriesTemplatesList]);

  // validate selected categories, templates
  useEffect(() => {
    if (!isAPIEditTemplateDataLoaded) {
      return;
    }

    const validationState = validateForm(categoriesValidationMapList,
      categoryTemplatesErrorsArrayList);
    setFormValidationState(validationState);

  }, [apiCategoriesTemplatesList, APICompliantToggleStatus, categoryTemplatesErrorsArrayList,
    categoriesValidationMapList, isAPIEditTemplateDataLoaded]);

  useEffect(() => {
    if (allTemplateCategories &&
        allTemplateCategories.length > 0) {
      const allTemplateCategoriesIds = map(allTemplateCategories, item => item.uuid);
      allTemplateCategoriesIds.forEach(uuid => {
        fetchCategoryTemplates(uuid, allTemplateCategoriesIds);
      })
    }
  }, [allTemplateCategories]);

  // If the user modifies the form after user asks the UI to autoresolve the issues
  // by clicking compliant button, we change the state to current from compliant
  const handleCategoryUpdate = (categoryIndex) => {
    categoryStateMapList[categoryIndex][STATE_CATEGORY_COMPLIANCE_TOGGLE_STATUS]
      = COMPLIANT_TOGGLE_BUTTON_CURRENT;
    categoryStateMapList[categoryIndex][STATE_CATEGORY_CURRENT_STATE_LIST] = undefined;
    setCategoryStateMapList(categoryStateMapList);
  };

  const removeTemplate = (categoryIndex, entityUuid, index) => {
    onFormEdit();
    if (!entityUuid || entityUuid === '0') {
      return;
    }
    const existingTemplates = apiCategoriesTemplatesList[categoryIndex] || [];
    existingTemplates.splice(index, 1);
    const templatesWithOrder = existingTemplates.map((item, index) => {
      item.ordinalInAPICategory = index + 1;
      return item;
    })
    apiCategoriesTemplatesList[categoryIndex] = templatesWithOrder;
    setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));
    handleCategoryUpdate(categoryIndex);
  };

  const moveTemplate = (categoryIndex, dragIndex, hoverIndex) => {
    onFormEdit();
    let apiCategoryTemplates = apiCategoriesTemplatesList[categoryIndex];
    const removedItems = apiCategoryTemplates.splice(dragIndex, 1);
    apiCategoryTemplates.splice(hoverIndex, 0, removedItems[0]);
    apiCategoryTemplates = map(apiCategoryTemplates, (item, index) => {
      item.ordinalInAPICategory = index + 1;
      return item;
    });
    apiCategoriesTemplatesList[categoryIndex] = apiCategoryTemplates;
    setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));
    handleCategoryUpdate(categoryIndex);
  };

  const handleChangeDynamicForm = (categoryIndex, val, name, index) => {
    onFormEdit();
    const apiCategoryTemplates = apiCategoriesTemplatesList[categoryIndex];
    const apiCategoryTemplate = apiCategoryTemplates[index];
    const args = apiCategoryTemplate.arguments;
    const idx = findIndex(args, item => item.name === name);
    if (name === RATE_LIMIT_QUOTA_LIST_FIELD) {
      const rateLimitQuotasForForm = getRateLimitQuotasForForm(props.rateLimitQuotas);
      apiCategoryTemplate.arguments = getRateLimitQuotaArguments(
        rateLimitQuotasForForm, val, false, apiCategoryTemplate.arguments,
        featureFlagForDistributeRateLimit,
      );
    } else {
      if (idx !== -1) {
        args[idx].value = val.toString();
        apiCategoryTemplate.arguments = args;
      }
    }
    apiCategoriesTemplatesList[categoryIndex] = apiCategoryTemplates;
    setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));
    handleCategoryUpdate(categoryIndex);
  };

  const addCategory = (uuid, index) => {
    addCategoryToStateLists(uuid, index);
  };

  const removeCategory = (index) => {
    removeCategoryFromStateLists(index);
  };

  const onMoveCategory = (dragIndex, dropIndex) => {
    moveCategoryInStateLists(dragIndex, dropIndex);
  };

  const addTemplate = (categoryIndex, categoryUuid, selectedTemplate, index) => {
    onFormEdit();
    let existingTemplates = apiCategoriesTemplatesList[categoryIndex] || [];
    const category = find(allTemplateCategories, category => category.uuid === categoryUuid);
    let templatesAreInOrder = false;
    if (category.enforceTemplateOrdering) {
      const existingFixedTemplates = filter(existingTemplates, item => !item.flexible);
      const orderArray = map(existingFixedTemplates, item => item.ordinal);
      templatesAreInOrder = isAscending(orderArray);
    }

    let positionToInsert = index;
    if (!selectedTemplate.flexible && category.enforceTemplateOrdering && templatesAreInOrder) {
      positionToInsert =
        findIndex(existingTemplates, template =>
          (!template.flexible && template.ordinal > selectedTemplate.ordinal),
        );
    }

    if (!isUndefined(positionToInsert)) {
      if (positionToInsert === -1) {
        existingTemplates.push(selectedTemplate);
      } else if (positionToInsert === 0) {
        existingTemplates.unshift(selectedTemplate);
      } else {
        existingTemplates.splice(positionToInsert, 0, selectedTemplate);
      }
      existingTemplates = map(existingTemplates, (item, index) => (
        {
          ...item,
          ordinalInAPICategory: index + 1,
        }));
    } else {
      selectedTemplate.ordinalInAPICategory = existingTemplates.length + 1;
      existingTemplates.push(selectedTemplate);
    }

    apiCategoriesTemplatesList[categoryIndex] = cloneDeep(existingTemplates);
    setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));
    handleCategoryUpdate(categoryIndex);
  };

  const getAPILevelRuleViolationComplianceLevel = (portalStatus, currentCompliance) => {
    return getViolationComplianceLevelForConfiguration(portalStatus, currentCompliance,
      enforceOrderingSetting);
  };

  const getCategoryLevelRuleViolationComplianceLevel =
    (categoryId, portalStatus, currentCompliance) => {
    const templateCategory = find(allTemplateCategories, item => item.uuid === categoryId);
    return getViolationComplianceLevelForConfiguration(portalStatus, currentCompliance,
      !(templateCategory && templateCategory.allowNonCompliantApisToBeUpdated));
  };

  const getViolationComplianceLevelForConfiguration =
    (portalStatus, currentCompliance, enforceRule) => {
      if (portalStatus === API_STATUS_NEW || portalStatus === API_STATUS_INCOMPLETE) {
        return VIOLATION_LEVEL_ADMIN_ERROR;
      } else if (currentCompliance === COMPLIANCE_STATUS_COMPLIANT) {
        return VIOLATION_LEVEL_ADMIN_OVERRIDABLE;
      } else if (enforceRule) {
        return VIOLATION_LEVEL_PUBLISHER_ERROR;
      }
      return VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE;
    };

  const handleCategoryCompliantToggle = (uuid, value, categoryIndex) => {
    onFormEdit();
    if (value === COMPLIANT_TOGGLE_BUTTON_COMPLIANT) {
      // If category compliant button is clicked,
      // try to fix autoresolvable issues
      const currentCategory = find(allTemplateCategories, item => item.uuid === uuid) || {};
      const { enforceTemplateOrdering, templateUsageRestriction } = currentCategory;
      categoryStateMapList[categoryIndex][STATE_CATEGORY_COMPLIANCE_TOGGLE_STATUS]
        = COMPLIANT_TOGGLE_BUTTON_COMPLIANT;
      categoryStateMapList[categoryIndex][STATE_CATEGORY_CURRENT_STATE_LIST]
        = cloneDeep(apiCategoriesTemplatesList[categoryIndex]);
      setCategoryStateMapList(categoryStateMapList);
      let existingTemplates = apiCategoriesTemplatesList[categoryIndex] || [];
      const existingTemplatesIds = map(existingTemplates, item => item.uuid);
      const categoryTemplates = categoriesTemplatesMap[uuid];

      // If template usage restriction is ALL, include all templates in the category
      if (templateUsageRestriction === USAGE_OPTION_ALL_TEMPLATES) {
        const categoryTemplatesIds = map(categoryTemplates, item => item.uuid);
        const templatesNeedTobeAddedIds = difference(categoryTemplatesIds, existingTemplatesIds);
        let ordinalOfNewTemplate = existingTemplates.length;
        forEach(templatesNeedTobeAddedIds, id => {
          const selectedTemplate = find(categoryTemplates,
            item => item.uuid === id);
          ordinalOfNewTemplate++;
          selectedTemplate.ordinalInAPICategory = ordinalOfNewTemplate;
          existingTemplates.push(selectedTemplate)
        });
      }

      // If the template ordering is enforced, sort the templates at the end
      if (enforceTemplateOrdering) {
        const fixedTemplates = []
        const flexibleTemplates = [];
        forEach(existingTemplates, item => {
          if (item.flexible) {
            flexibleTemplates.push(item);
          } else {
            fixedTemplates.push(item);
          }
        });
        const templatesOrdinalMap = {};
        forEach(categoryTemplates, item => {
          templatesOrdinalMap[item.uuid] = item.ordinal;
        });
        const sortedFixedTemplates = sortBy(fixedTemplates, item => templatesOrdinalMap[item.uuid]);
        existingTemplates = [...sortedFixedTemplates, ...flexibleTemplates];
      }
      apiCategoriesTemplatesList[categoryIndex] = existingTemplates;
      setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));
    } else if (value === COMPLIANT_TOGGLE_BUTTON_CURRENT) {
      categoryStateMapList[categoryIndex][STATE_CATEGORY_COMPLIANCE_TOGGLE_STATUS]
        = COMPLIANT_TOGGLE_BUTTON_CURRENT;
      apiCategoriesTemplatesList[categoryIndex]
        = categoryStateMapList[categoryIndex][STATE_CATEGORY_CURRENT_STATE_LIST];
      setApiCategoriesTemplatesList(cloneDeep(apiCategoriesTemplatesList));
      categoryStateMapList[categoryIndex][STATE_CATEGORY_CURRENT_STATE_LIST] = undefined;
      setCategoryStateMapList(categoryStateMapList);
    }
  };

  const categoryHasAutoResolvableIssues = (categoryErrors, categoryDetails) => {
    const categoryHasOrderingIssue = categoryErrors[CATEGORY_CONFIG_ORDER];
    const categoryHasUsageIssue = categoryErrors[CATEGORY_CONFIG_USAGE];
    const { templateUsageRestriction } = categoryDetails;
    return categoryHasOrderingIssue ||
      (categoryHasUsageIssue && templateUsageRestriction === USAGE_OPTION_ALL_TEMPLATES);
  };
  const updateArgumentsToClipboard = (categoryIndex, templateIndex) => {
    const apiCategoryTemplates = apiCategoriesTemplatesList[categoryIndex];
    const apiCategoryTemplate = apiCategoryTemplates[templateIndex];
    const args = apiCategoryTemplate.arguments;
    const argNameValueMap = {};
    args.filter(arg=> !arg.secure).forEach(arg => argNameValueMap[arg.name] = arg.value);
    if(Object.keys(argNameValueMap).length > 0) {
      setArgumentClipboard({
        [apiCategoryTemplate.uuid]: argNameValueMap,
      });
    notifyMessages(intl.getI18nMessage('label.api.edit.policy.templates.policy.template.arguments.copied'), ALERT_SUCCESS);
    setTimeout(() => notifyMessages('', ''), 1000);
    }
    
  }
  const updateArgumentsFromClipboard = (categoryIndex, templateIndex) => {
    const categoryTemplateList = cloneDeep(apiCategoriesTemplatesList);
    const apiCategoryTemplates = categoryTemplateList[categoryIndex];
    const apiCategoryTemplate = apiCategoryTemplates[templateIndex];
    const argNameValueMap = argumentClipboard[apiCategoryTemplate.uuid];
    const templateArgs = apiCategoryTemplate.arguments;
    templateArgs.forEach(templateArg => {
      if(argNameValueMap[templateArg.name]){
        templateArg.value = argNameValueMap[templateArg.name];
      }
    });
    setApiCategoriesTemplatesList(categoryTemplateList);
  }
  const renderTemplateCategory = (uuid, index) => {
    const currentCategory = find(allTemplateCategories, item => item.uuid === uuid) || {};
    const categoryName = get(currentCategory, 'name', '');
    const apiCategoryTemplates = apiCategoriesTemplatesList[index] || [];
    const categoryTemplates = categoriesTemplatesMap && categoriesTemplatesMap[uuid] || [];
    const availableCategoryTemplates = filter(categoryTemplates, item => item.name);
    const complianceToggleStatus =
      (categoryStateMapList[index]
      && categoryStateMapList[index][STATE_CATEGORY_COMPLIANCE_TOGGLE_STATUS])
      || COMPLIANT_TOGGLE_BUTTON_CURRENT;
    // Don't show compliance toggle buttons for non-compliant categories
    const instances = filter(categoriesOrderToRender, id => id === uuid)
    const isDuplicatedCategory = instances.length > 1;
    const isDuplicatedFixedCategoryId = isFixedCategoryId(uuid) && isDuplicatedCategory;
    const isNonCompliantCategory = isNonApplicableCategoryId(uuid) || isDuplicatedFixedCategoryId;
    const categoryTemplatesErrors = categoryTemplatesErrorsArrayList[index] || [];
    const categoryDetails = find(allTemplateCategories, item => item.uuid === uuid) || {};
    const categoryWiseValidationErrorsMap = categoriesValidationMapList[index] || {};
    const savedCurrentModeState = categoryStateMapList[index]
      && categoryStateMapList[index][STATE_CATEGORY_CURRENT_STATE_LIST];
    // show compliance mode toggle if there is autoresolvable issue or have a saved state
    // saved state is available when user chooses compliant mode
    // it will be cleared if user edits the category
    const showCategoryComplianceToggle =
      categoryHasAutoResolvableIssues(categoryWiseValidationErrorsMap, categoryDetails) ||
      savedCurrentModeState;
    const complianceLevel =
      getCategoryLevelRuleViolationComplianceLevel(uuid, portalStatus, compliance);

    let canDelete = true;
    // if the category is fixed and usage is not optional
    // don't allow removal of last instance
    if (!isNonCompliantCategory && isFixedCategory(currentCategory)
      && !isCategoryUsageOptional(currentCategory)) {
      const instances = filter(categoriesOrderToRender, id => id === uuid);
      if (instances.length === 1) {
        canDelete = false;
      }
    }
    const isUserAdmin = hasAdminRole(userContext);
    return (
      <Category
        addTemplate={
          (categoryUuid, selectedTemplate, templateIndex) =>
            addTemplate(index, categoryUuid, selectedTemplate, templateIndex)
        }
        alwaysAllowOrderingTemplates={isUserAdmin}
        associatedTemplates={apiCategoryTemplates}
        availableTemplates={availableCategoryTemplates}
        canRemove={canDelete}
        categoryDetails={categoryDetails}
        categoryIndex={index}
        categoryWiseValidationErrorsMap={categoryWiseValidationErrorsMap}
        categoryTemplatesErrors={categoryTemplatesErrors}
        complianceLevel={complianceLevel}
        complianceToggleStatus={complianceToggleStatus}
        expandAllTemplates={expandAllTemplates}
        handleChangeDynamicForm={
          (categoryUuid, val, name, templateIndex) =>
            handleChangeDynamicForm(index, val, name, templateIndex)
        }
        handleCategoryCompliantToggle={
          (uuid, value) => handleCategoryCompliantToggle(uuid, value, index)
        }
        isNonCompliantCategory={isNonCompliantCategory}
        moveTemplate={
          (categoryUuid, dragIndex, hoverIndex) => moveTemplate(index, dragIndex, hoverIndex)
        }
        name={categoryName}
        onMoveCategory={onMoveCategory}
        removeCategory={() => removeCategory(index)}
        removeTemplate={
          (categoryUuid, entityUuid, templateIndex) =>
            removeTemplate(index, entityUuid, templateIndex)
        }
        showCategoryCompliance={showCategoryComplianceToggle}
        uuid={uuid}
        onCopyArguments={(categoryIndex, templateIndex) =>
              updateArgumentsToClipboard(categoryIndex, templateIndex)}
        onPasteArguments={(categoryIndex, templateIndex) =>
              updateArgumentsFromClipboard(categoryIndex, templateIndex)}
        argumentClipboard={argumentClipboard}
      />
    );
  };

  const renderTemplateCategories = () => {
    const renderDropTarget = (index) => (
      <EntityDropTarget
        addEntity={(uuid, index) => {
          addCategory(uuid, index);
          setShowCategoryDropTargets(false);
        }}
        index={index}
        showDropTargets={showCategoryDropTargets}
        type={ENTITY_PICKER_TYPE_CATEGORY}
      />);
    if (apiApplicableTemplateCategories.length === 0) {
      return (<div>
        {renderNoCategoriesToRenderError()}
      </div>);
    }
    if (!categoriesOrderToRender || (categoriesOrderToRender.length === 0)) {
      return (<div>
        {renderDropTarget(0)}
      </div>);
    }
    return (<div>
      {renderDropTarget(0)}
      {categoriesOrderToRender.map(
        (uuid, index) => {
          return (<div key={`${uuid}-${index}-wrapper`}>
            {renderTemplateCategory(uuid, index)}
            {renderDropTarget(index + 1)}
          </div>);
        },
      )}
    </div>);
  };

  const renderCategoryPicker = () => {
    if (!APICategoriesMissing) {
      return null;
    }
    
    return (<EntityPicker
      entities={apiApplicableFlexibleMissingCategories}
      hasError={APICategoriesMissingViolationLevel !== VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE}
      message={intl.getI18nMessage('label.api.edit.entity.picker.category.title.text')}
      onDrag={(value) => setShowCategoryDropTargets(value)}
      type={ENTITY_PICKER_TYPE_CATEGORY}
    />);
  };

  const onNextPolicyTemplates = () => {
    const data = [];
    forEach(categoriesOrderToRender, (templateCategoryUuid, index) => {
      const categoryTemplates = apiCategoriesTemplatesList[index];
      forEach(categoryTemplates, ({ uuid, arguments:args }) => {
        let argsPayload = [];
        const rateLimitPolicyEntity = getRateLimitQuotaPolicyTemplates(args);
        if (rateLimitPolicyEntity) {
          //Added arguments for rateQuotaUuid and rateQuotaJson
          const argumentsRqUuid = args.find(o => o.name === 'l7.rateQuotaUuid') || '';
          const argumentsRqJson = args.find(o => o.name === 'l7.rateQuotaJson') || '';
          //Assign rateQuotaJson value as empty string when rqJson value is null
          const jsonValue = get(argumentsRqJson, 'value') ? get(argumentsRqJson, 'value') : '';
          argumentsRqJson.value = jsonValue;
          argsPayload = [argumentsRqUuid, argumentsRqJson];
        } else {
          // removed args whose value is empty
          const filteredArgs = filter(args, ({ value }) => value);
          argsPayload = map(filteredArgs, ({ name, value }) => {
            return ({
              name,
              value,
            });
          });
        }
        data.push({
          policyEntityUuid: uuid,
          templateCategoryUuid,
          policyTemplateArguments: argsPayload,
        });
      });
    });
    saveApiPolicyEntities(apiUuid, data);
  };

  const onSave = () => {
    localStorage.setItem('isUnSavedChanges', false);
    // setIsSelectedRateQuotaTemplate(false);
    onNextPolicyTemplates();
  };

  const onNext = () => {
    localStorage.setItem('isUnSavedChanges', false);
    goToNextTab();
  };

  const getCompliantModeCategoriesOrderAPITemplatesStateMapList = () => {
    // remove non-compliant categories association from currentModeCategoriesRenderOrder
    // and apiCategoriesTemplatesList
    // filterout fixed categories and check whether they are sorted
    // if they are sorted, return list of categories
    // if they are not sorted, filterout flexible categories,
    // return sorted fixed categories + flexible categories

    // apiCategoriesTemplatesList
    // currentModeCategoriesRenderOrder
    // categoryStateMapList

    // creating non-compliant category indexes and remove them from
    // the lists which holds page info:
    // apiCategoriesTemplatesList, currentModeCategoriesRenderOrder, categoryStateMapList
    // creating non-compliant category indexes
    const compliantModeCategoriesRenderOrder = cloneDeep(currentModeCategoriesRenderOrder);
    const nonCompliantCategoryIndexes = [];
    const fixedCategoriesIds
      = filter(compliantModeCategoriesRenderOrder, id => isFixedCategoryId(id));
    const duplicatedFixedCategoriesIds = getDuplicatedIds(fixedCategoriesIds);
    // map to mark the visited duplicated fixed categories
    // first instances of those categories will remain, other instances will be removed
    const visitedDuplicatedCategoriesMap = {};
    // values of first instances should be cleared, so that user can see fresh category
    const indexesOfFirstInstancesOfDuplicatedCategories = [];
    const compliantToggleStateArray = [];
    forEach(compliantModeCategoriesRenderOrder, (id, index) => {
      if (isNonApplicableCategoryId(id)) {
        nonCompliantCategoryIndexes.push(index);
      } else if (duplicatedFixedCategoriesIds.includes(id)) {
        if (visitedDuplicatedCategoriesMap[id]) {
          nonCompliantCategoryIndexes.push(index);
        } else {
          visitedDuplicatedCategoriesMap[id] = index;
          indexesOfFirstInstancesOfDuplicatedCategories.push(index);
          compliantToggleStateArray.push(getDefaultStateMap(allTemplateCategoriesMap[id]));
        }
      }
    });
    // make copy of the lists and remove non-compliant categories info
    const apiCategoriesTemplatesListCopy = cloneDeep(apiCategoriesTemplatesList);
    const compliantModeCategoriesRenderOrderCopy = compliantModeCategoriesRenderOrder;
    // sorting in reverse order, so that removed elements doesn't affect position
    // of elements to be deleted.
    const reverseSortedNonCompliantCategoryIndexes = sortBy(nonCompliantCategoryIndexes).reverse();
    const categoryStateMapListCopy = cloneDeep(categoryStateMapList);
    forEach(indexesOfFirstInstancesOfDuplicatedCategories, index => {
      apiCategoriesTemplatesListCopy[index] = [];
      categoryStateMapListCopy[index] = compliantToggleStateArray[index];
    });
    forEach(reverseSortedNonCompliantCategoryIndexes, position => {
      apiCategoriesTemplatesListCopy.splice(position, 1);
      compliantModeCategoriesRenderOrderCopy.splice(position, 1);
      categoryStateMapListCopy.splice(position, 1);
    });

    // check whether fixed categories are in order
    const fixedCategories = compliantModeCategoriesRenderOrderCopy
      .map(id => find(allTemplateCategories, item => item.uuid === id))
      .filter(item => !item.flexible);
    const fixedCategoriesOrdinalArray = map(fixedCategories, item => item.ordinal);
    if (!isAscending(fixedCategoriesOrdinalArray)) {
      // create separate arrays for fixed and flexible
      // sort fixed array, update statelist, join both arrays and send
      const flexibleCategories = compliantModeCategoriesRenderOrderCopy
        .map(id => find(allTemplateCategories, item => item.uuid === id))
        .filter(item => item.flexible);
      const fixedCategoriesIndexes = [];
      const flexibleCategoriesIndexes = [];
      forEach(compliantModeCategoriesRenderOrderCopy, (id, index) => {
        if (find(fixedCategories, item => item.uuid === id)) {
          fixedCategoriesIndexes.push(index);
        }
        if (find(flexibleCategories, item => item.uuid === id)) {
          flexibleCategoriesIndexes.push(index);
        }
      });

      // creating separate arrays for fixed and flexible
      const fixedApiCategoriesTemplatesList = [];
      const fixedCompliantModeCategoriesRenderOrder = [];
      const fixedStateMapList = [];
      forEach(fixedCategoriesIndexes, index => {
        fixedApiCategoriesTemplatesList.push(apiCategoriesTemplatesListCopy[index]);
        fixedCompliantModeCategoriesRenderOrder.push(compliantModeCategoriesRenderOrderCopy[index]);
        fixedStateMapList.push(categoryStateMapListCopy[index]);
      });
      const flexibleApiCategoriesTemplatesList = [];
      const flexibleCurrentModeCategoriesRenderOrder = [];
      const flexibleStateMapList = [];
      forEach(flexibleCategoriesIndexes, index => {
        flexibleApiCategoriesTemplatesList.push(apiCategoriesTemplatesListCopy[index]);
        flexibleCurrentModeCategoriesRenderOrder.push(
          compliantModeCategoriesRenderOrderCopy[index],
        );
        flexibleStateMapList.push(categoryStateMapListCopy[index]);
      });

      // add an attribute location before sorting fixed categories and
      // use it after sorting to update
      // apiCategoriesTemplatesList, currentModeCategoriesRenderOrder, categoryStateMapList
      const fixedCategoriesCopy = map(fixedCategories,
        (item, index) => ({ ...item, location: index }));
      const sortedFixedCategoriesCopy = sortBy(fixedCategoriesCopy, item => item.ordinal);
      forEach(fixedCategoriesCopy, (item, index) => {
        const position = findIndex(sortedFixedCategoriesCopy, item => item.location === index);
        apiCategoriesTemplatesListCopy[position]
          = fixedApiCategoriesTemplatesList[index];
        compliantModeCategoriesRenderOrderCopy[position]
          = fixedCompliantModeCategoriesRenderOrder[index];
        categoryStateMapListCopy[position] = fixedStateMapList[index];
      });

      const fixedCategoriesCount = fixedCategories.length;
      for(let index = 0; index < flexibleCategories.length; index++) {
        apiCategoriesTemplatesListCopy[fixedCategoriesCount + index]
          = flexibleApiCategoriesTemplatesList[index];
        compliantModeCategoriesRenderOrderCopy[fixedCategoriesCount + index]
          = flexibleCurrentModeCategoriesRenderOrder[index];
        categoryStateMapListCopy[fixedCategoriesCount + index] = flexibleStateMapList[index];
      }
    }

    return {
      apiCategoriesTemplatesList: apiCategoriesTemplatesListCopy,
      compliantModeCategoriesRenderOrder: compliantModeCategoriesRenderOrderCopy,
      categoryStateMapList: categoryStateMapListCopy,
    };
  };

  const handleAPICompliantToggle = (value) => {
    onFormEdit();
    if (value) {
      setAPICompliantToggleStatus(value);
    }
    if (value === COMPLIANT_TOGGLE_BUTTON_COMPLIANT) {
      setSavedAPICurrentState(cloneDeep(apiCategoriesTemplatesList));
      const {
        apiCategoriesTemplatesList: newApiCategoriesTemplatesList,
        compliantModeCategoriesRenderOrder: newCompliantModeCategoriesRenderOrder,
        categoryStateMapList: newCategoryStateMapList,
      } = getCompliantModeCategoriesOrderAPITemplatesStateMapList();
      setApiCategoriesTemplatesList(newApiCategoriesTemplatesList);
      setCompliantModeCategoriesRenderOrder(newCompliantModeCategoriesRenderOrder);
      setCategoryStateMapList(newCategoryStateMapList);
    } else {
      setApiCategoriesTemplatesList(cloneDeep(savedAPICurrentState));
      setSavedAPICurrentState({});
    }
  };

  const renderAddCategoryDropdown = () => {
    const options = [];
    forEach(apiApplicableTemplateCategories, item => {
      const index = findIndex(categoriesOrderToRender, id => id === item.uuid);
      options.push({
        ...item,
        disabled: !item.flexible && (index !== -1),
      });
    });

    if (options.length === 0) {
      return null;
    }
    const handleChange = (value) => {
      const selectedCategory =
        find(allTemplateCategories, item => item.uuid === value);
      addCategory(selectedCategory.uuid);
    }

    return (<DropDownMenu
      customClasses={{
        menuListRoot: classes.categoriesMenuList,
        popper: classes.popper,
      }}
      handleChange={handleChange}
      data={options}
      id="add-category-dropdown"
      label={intl.getI18nMessage('label.api.edit.policy.templates.add.category')}
      value={null}
    />)
  };

  const renderAPILevelErrorAtLeastOneTemplate = () =>
    (<div
      className={classes.apiCategoryNonCompliantConfigItem}
      data-apim-test='no-templates-issue'
    >
      <InfoOutlinedIcon />
      <span className={classes.complianceInfoText}>
        {intl.getI18nMessage('label.api.edit.api.compliance.no.template.info.help.text')}
      </span>
    </div>);
  
  const renderAPILevelErrorNonCompliantCategoryExists = () =>
    (<div className={classes.apiCategoryNonCompliantConfigItem}>
      <ErrorRoundedIcon className={classes.complianceError} />
      <span className={classes.complianceErrorText}>
        {intl.getI18nMessage('label.api.edit.api.compliance.non.compliant.help.text')}
      </span>
    </div>);
  
  const renderAPILevelErrorMissingCategories = () => {
    const isWarning = APICategoriesMissingViolationLevel === VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE;
    const textClass = isWarning ? classes.complianceWarningText : classes.complianceErrorText;
    return (<div
      className={classes.apiCategoryOrderConfigItem}
      data-apim-test='missing-category-issue'
    >
      {!isWarning ?
        <ErrorRoundedIcon className={classes.complianceError} />
          :
        <img
          src={WarningIcon}
          className={classes.complianceWarning}
          alt="Compliance Warning"
        />
      }
      <span
        className={textClass}
        data-issue-type={isWarning ? 'warning' : 'error'}
      >
        {intl.getI18nMessage('label.api.edit.api.compliance.missing.categories.help.text')}
      </span>
    </div>);
  };

  const renderAPILevelWarningsErrors = () => {
    if (APICompliantToggleStatus === COMPLIANT_TOGGLE_BUTTON_CURRENT) {
      const complianceLevel
          = getAPILevelRuleViolationComplianceLevel(portalStatus, compliance);
      const isWarning = complianceLevel === VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE;
      const orderConfigClassName = isWarning ?
        classes.complianceWarning : classes.complianceError;
      const orderConfigTextClassName = isWarning ?
        classes.complianceWarningText : classes.complianceErrorText;
      return (<div className={classes.apiCategoryConfigsList}>
        {!APIHasCategoriesInOrder &&
        <div
          className={classes.apiCategoryOrderConfigItem}
          data-apim-test='category-order-issue'
        >
          {!isWarning ?
            <ErrorRoundedIcon className={classes.complianceError} />
              :
            <img
              src={WarningIcon}
              className={orderConfigClassName}
              alt="Compliance Warning"
            />
          }
          <span
            className={orderConfigTextClassName}
            data-issue-type={isWarning ? 'warning' : 'error'}
          >
            {intl.getI18nMessage('label.api.edit.api.compliance.order.allow.help.text')}
          </span>
        </div>}
        {!APIHasAtLeastOneTemplate && renderAPILevelErrorAtLeastOneTemplate()}
        {(APIHasNonApplicableCategories || APIHasDuplicateFixedCategories) &&
          renderAPILevelErrorNonCompliantCategoryExists()}
        {APICategoriesMissing && renderAPILevelErrorMissingCategories()}
      </div>);
    } else if (APICompliantToggleStatus === COMPLIANT_TOGGLE_BUTTON_COMPLIANT) {
      return (<div className={classes.apiCategoryConfigsList}>
        {!APIHasAtLeastOneTemplate && renderAPILevelErrorAtLeastOneTemplate()}
        {APICategoriesMissing && renderAPILevelErrorMissingCategories()}
      </div>);
    }
    return (<></>);
  };

  const renderNoCategoriesToRenderError = () =>
    (<div
      className={classes.noCategoriesErrorContainer}
      data-apim-test='no-categories-issue'
    >
      <div><ErrorRoundedIcon className={classes.complianceError} /></div>
      <div>{intl.getI18nMessage('label.api.edit.api.compliance.no.categories.error.help.text')}</div>
    </div>);

  // 4 behaviours
  // 1. no violation: show 'Cancel' and 'Save & Next' buttons
  // 2. warnings: show 'Cancel' and 'Save & Next' buttons with warning help text
  // 3. admin overridable issue: show 'Cancel', 'Force Save & Next' and 'Next'
  // with error help text. Next button should have warning icon and tooltip
  // 4. error: show 'Cancel', 'Save' and 'Next' buttons with error help text.
  // Save should be disabled. Next button should have warning icon and tooltip

  const renderFooter = () => {
    const isUserAdmin = hasAdminRole(userContext);
    const noViolation = (formValidationState === VIOLATION_LEVEL_NO_VIOLATION);
    const hasWarnings = (formValidationState === VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE);
    const isForceSave = (isUserAdmin &&
      ((formValidationState === VIOLATION_LEVEL_ADMIN_OVERRIDABLE) ||
      (formValidationState === VIOLATION_LEVEL_PUBLISHER_ERROR)));
    const hasErrors = (formValidationState === VIOLATION_LEVEL_ADMIN_ERROR) ||
      (!isUserAdmin && ((formValidationState === VIOLATION_LEVEL_ADMIN_OVERRIDABLE) ||
      (formValidationState === VIOLATION_LEVEL_PUBLISHER_ERROR)));
    const showAdditional = hasErrors || isForceSave;
    let nextText = '';
    const nextBtnContentForErros =
      (<Tooltip
        arrow
        title={intl.getI18nMessage('label.api.edit.policy.templates.form.warning.button.tooltip.text')}
      >
        <div className={classes.nextBtnContent}>
          <div>{NEXT_TEXT}</div>
          <div className={classes.nextBtnIcon}>
            <img
              src={WarningIcon}
              className={classes.complianceWarning}
              alt="Compliance Warning"
            />
          </div>
        </div>
      </Tooltip>);
    const onNextClick = (noViolation || hasWarnings) ? onSave : onNext;
    let assistText = '';
    let additionalText = intl.getI18nMessage('label.save.button');

    if (isForceSave) {
      assistText = (<div className={classes.assistTextContainer}>
          <div className={classes.assistTextIcon}><InfoOutlinedIcon /></div>
          <div className={classes.assistText}>
            {intl.getI18nMessage('label.api.edit.category.compliance.force.save.assist.text')}
          </div>
        </div>);
      nextText = nextBtnContentForErros;
      additionalText = intl.getI18nMessage('label.force.save.next.button');
    } else if (hasErrors) {
      let toolTip = intl.getI18nMessage('label.api.edit.policy.templates.form.compliance.error.tooltip.text');
      let message = intl.getI18nMessage('label.api.edit.policy.templates.form.compliance.error.help.text');
      if (formHasTemplateErrors) {
        message = intl.getI18nMessage('label.api.edit.policy.templates.form.templates.error.help.text');
        toolTip = intl.getI18nMessage('label.api.edit.policy.templates.form.templates.error.tooltip.text');
      }
      assistText =
        (
      <Tooltip
        arrow
        title={toolTip}
      >
        <div className={classes.assistTextContainer}>
          <div className={classes.assistTextIcon}>
            <ErrorRoundedIcon className={classes.complianceError} />
          </div>
          <div className={classes.assistText}>
            {message}
          </div>
        </div>
      </Tooltip>);
      nextText = nextBtnContentForErros;
    } else if (hasWarnings) {
      assistText =
        (
      <Tooltip
        arrow
        title={intl.getI18nMessage('label.api.edit.policy.templates.form.warning.tooltip.text')}
      >
        <div className={classes.assistTextContainer}>
          <div className={classes.assistTextIcon}><img
            src={WarningIcon}
            className={classes.complianceWarning}
            alt="Compliance Warning"
          /></div>
          <div className={classes.assistText}>
            {intl.getI18nMessage('label.api.edit.policy.templates.form.warning.help.text')}
          </div>
        </div>
      </Tooltip>);
      nextText = SAVE_AND_NEXT_TEXT;
    } else {
      nextText = SAVE_AND_NEXT_TEXT;
    }

    return (
      <FormActionButtons
        additionalText={additionalText}
        assistText={assistText}
        customClasses={{
            additionalButton: isForceSave ? classes.footerForceSaveBtn : '',
            nextButton: classes.footerNextBtn,
          }}
        id="visibility-form-buttons"
        isDisabledAdditional={hasErrors}
        nextText={nextText}
        onAdditionalClick={onSave}
        onCancelClick={() => onCancel(isFormEdited())}
        onNextClick={onNextClick}
        showAdditional={showAdditional}
      />
    );
  };

  const toggleExpandTemplates = () => {
    setExpandAllTemplates(!expandAllTemplates);
  };

  const addAdditionalInfoToErrors = useMemo(() => {
    const updatedErrors = map(errors, error => {
      if (error.key === 'error.api.update.policytemplate.category.donthave.category.templates.in.order') {
        // replace category uuid with category name
        // to help user to interpret the error message
        const components = split(error.field, '.');
        const category = find(allTemplateCategories, item => item.uuid === components[1]);
        const name = category.name;
        if ((components.length === 2) && (components[0] === 'categoryUuid')
          && name) {
            return ({
              ...error,
              field: `Category ${name}`,
            });
        }
      }
      return error;
    });
    return updatedErrors;
  }, [errors]);

  return (
    <div id="api-category-policy-container">
      <DndProvider backend={HTML5Backend}>
        <div className={classes.pageTitleContainer}>
          <div>
            <h1 className={classes.pageTitle}>{intl.getI18nMessage('label.api.edit.policy.templates.title')}</h1>
          </div>
          <div className={classes.apiRightBtnGroup}>
            {showAPILevelCompliantSwitch && <div>
              <FormButtonGroup
                id={'api-compliance-toggle-btn-group'}
                name={''}
                value={APICompliantToggleStatus}
                data={getComplianceToggleOptions(intl)}
                handleChange={handleAPICompliantToggle}
                filedContainerClass={classes.apiComplianceBtnGroup}
              />
            </div>}
            {renderAddCategoryDropdown()}
          </div>
        </div>
        <div className={classes.expandTemplatesContainer}>
          <Button
            color="primary"
            size="small"
            onClick={toggleExpandTemplates}
            variant="text"
            id="api-templates-expand-all-templates"
            data-apim-test="api-templates-expand-all-templates"
          >
            {expandAllTemplates ?
              intl.getI18nMessage('label.api.edit.policy.templates.collapse.all.buttons')
              :
              intl.getI18nMessage('label.api.edit.policy.templates.expand.all.buttons')
            }
          </Button>
        </div>
        {isAPIEditTemplateDataLoaded && renderAPILevelWarningsErrors()}
        {errors.length > 0 &&
          <ErrorContainer errors={addAdditionalInfoToErrors} />
        }
        {
          (isAPIEditTemplateDataLoaded && renderCategoryPicker())
        }
        {
          (isAPIEditTemplateDataLoaded && renderTemplateCategories())
        }
        <div className={classes.greyBand} />
        {renderFooter()}
      </DndProvider>
    </div>
  );
}

const mapDispatchToProps = {
  fetchAPIApplicableTemplateCategoryList,
  fetchAPISettingsEnforceCategoryOrdering,
  fetchCategoryTemplates,
  fetchTemplateCategoryDetailsForMap,
  fetchTemplateCategoryList,
};

const mapStateToProps = (state) => ({
  additionalCategoriesDetailsMap: getTemplateCategoriesDetailsMap(state),
  apiApplicableTemplateCategories: getAPIApplicableTemplateCategoryListResults(state),
  enforceOrderingSetting: getAPISettingsEnforceCategoryOrdering(state),
  isAccessibleTemplateCategoriesLoaded: getIsAccessibleTemplateCategoriesLoaded(state),
  isAPIEditTemplateDataLoaded: getIsAPIEditTemplateDataLoaded(state),
  allAccessibleTemplateCategories: getTemplateCategoryListResults(state),
  categoriesTemplatesMapParam: getCategoriesTemplatesMap(state),
});

CategoryPolicyTemplates.propTypes = {
  additionalCategoriesDetailsMap: PropTypes.object,
  allAccessibleTemplateCategories: PropTypes.array,
  apiApplicableTemplateCategories: PropTypes.array,
  apiDetails: PropTypes.object,
  apiUuid: PropTypes.string,
  categoriesTemplatesMapParam: PropTypes.object,
  classes: PropTypes.object,
  enforceOrderingSetting: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.object),
  fetchAPIApplicableTemplateCategoryList: PropTypes.func,
  fetchAPISettingsEnforceCategoryOrdering: PropTypes.func,
  fetchCategoryTemplates: PropTypes.func,
  fetchRateLimitQuotas: PropTypes.func,
  fetchTemplateCategoryDetailsForMap: PropTypes.func,
  fetchTemplateCategoryList: PropTypes.func,
  goToNextTab: PropTypes.func,
  isAccessibleTemplateCategoriesLoaded: PropTypes.bool,
  isAPIEditTemplateDataLoaded: PropTypes.bool,
  onCancel: PropTypes.func,
  rateLimitQuotas: PropTypes.arrayOf(PropTypes.object),
  saveApiPolicyEntities: PropTypes.func,
  tabValue: PropTypes.string,
  userContext: PropTypes.object,
  featureFlagForDistributeRateLimit: PropTypes.bool,
  notifyMessages: PropTypes.func,
};

CategoryPolicyTemplates.displayName = 'APICategoryPolicyTemplates';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(CategoryPolicyTemplates);
